import { LEGALSHIELD_LINKS } from '../../../config/legalshield-links';

export default function ChooserIntro() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="chooser" className="relative py-20 bg-[#0a1628]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6" style={{ fontFamily: "'Roboto', sans-serif" }}>
            WHAT ARE YOU TRYING TO <span className="text-[#DC2626]">PROTECT?</span>
          </h2>
        </div>

        {/* Three Choice Cards */}
        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {/* Card A - Personal / Family */}
          <div className="bg-gradient-to-br from-[#0f1d2e] to-[#1a2332] rounded-2xl p-6 sm:p-8 border-2 border-[#DC2626]/20 hover:border-[#DC2626] transition-all duration-300 hover:scale-105">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#DC2626]/20 rounded-full flex items-center justify-center mb-4 sm:mb-6">
              <i className="ri-user-heart-fill text-3xl sm:text-4xl text-[#DC2626]"></i>
            </div>
            
            <h3 className="text-xl sm:text-2xl font-black text-white mb-4">
              Just Me &amp; My Family
            </h3>
            
            <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-300 text-sm sm:text-base">Traffic tickets &amp; moving violations</span>
              </li>
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-300 text-sm sm:text-base">Shady contractors &amp; home repairs</span>
              </li>
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-300 text-sm sm:text-base">HOA drama &amp; neighbor disputes</span>
              </li>
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-300 text-sm sm:text-base">Insurance claim denials</span>
              </li>
            </ul>
            
            <button
              onClick={() => scrollToSection('path-personal')}
              className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-black text-sm sm:text-base md:text-lg px-4 sm:px-6 py-3 sm:py-4 rounded-lg transition-all duration-300 cursor-pointer leading-tight text-center whitespace-nowrap"
            >
              PROTECT MYSELF &amp; FAMILY
            </button>
          </div>

          {/* Card B - Landlord / Business */}
          <div className="bg-gradient-to-br from-[#0f1d2e] to-[#1a2332] rounded-2xl p-6 sm:p-8 border-4 border-[#DC2626] shadow-2xl shadow-[#DC2626]/20 relative">
            <div className="absolute top-0 left-0 right-0 bg-[#DC2626] text-white text-center py-2 font-black text-xs sm:text-sm uppercase tracking-wider rounded-t-xl">
              Most Common
            </div>
            
            <div className="pt-6 sm:pt-8">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#DC2626]/20 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-building-fill text-3xl sm:text-4xl text-[#DC2626]"></i>
              </div>
              
              <h3 className="text-xl sm:text-2xl font-black text-white mb-4">
                My Rentals or My Business
              </h3>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-300 text-sm sm:text-base">Tenant disputes &amp; evictions</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-300 text-sm sm:text-base">Vendor &amp; contractor issues</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-300 text-sm sm:text-base">LLC formation &amp; business contracts</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-300 text-sm sm:text-base">Partnership &amp; employment disputes</span>
                </li>
              </ul>
              
              <button
                onClick={() => scrollToSection('path-business')}
                className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-black text-xs sm:text-sm md:text-base px-3 sm:px-4 py-3 sm:py-4 rounded-lg transition-all duration-300 cursor-pointer shadow-xl leading-tight text-center whitespace-nowrap"
              >
                PROTECT MY RENTALS / BUSINESS
              </button>
            </div>
          </div>

          {/* Card C - Identity Only */}
          <div className="bg-gradient-to-br from-[#0f1d2e] to-[#1a2332] rounded-2xl p-6 sm:p-8 border-2 border-[#DC2626]/20 hover:border-[#DC2626] transition-all duration-300 hover:scale-105">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#DC2626]/20 rounded-full flex items-center justify-center mb-4 sm:mb-6">
              <i className="ri-shield-keyhole-fill text-3xl sm:text-4xl text-[#DC2626]"></i>
            </div>
            
            <h3 className="text-xl sm:text-2xl font-black text-white mb-4">
              Just My Identity &amp; Credit
            </h3>
            
            <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-300 text-sm sm:text-base">Identity theft protection</span>
              </li>
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-300 text-sm sm:text-base">Dark web monitoring</span>
              </li>
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-300 text-sm sm:text-base">Credit monitoring (all 3 bureaus)</span>
              </li>
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-300 text-sm sm:text-base">Full restoration services</span>
              </li>
            </ul>
            
            <button
              onClick={() => scrollToSection('path-identity')}
              className="w-full bg-white/10 hover:bg-white/20 text-white font-black text-sm sm:text-base md:text-lg px-4 sm:px-6 py-3 sm:py-4 rounded-lg transition-all duration-300 cursor-pointer border-2 border-[#DC2626]/30 hover:border-[#DC2626] leading-tight text-center whitespace-nowrap"
            >
              PROTECT MY IDENTITY
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
